import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Button, Space, Select, message, Divider } from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, MessageOutlined, CalendarOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

const { Option } = Select;

const ContactMessageDetailModal = ({ submission, isOpen, onClose, onStatusUpdate }) => {
  const [status, setStatus] = useState(submission?.status || 'new');
  const [updating, setUpdating] = useState(false);

  if (!submission) return null;

  const getStatusConfig = (status) => {
    const configs = {
      'new': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'New' },
      'read': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Read' },
      'resolved': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Resolved' },
    };
    return configs[status] || configs['new'];
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    try {
      await onStatusUpdate(submission._id, status);
    } catch (error) {
      message.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const statusConfig = getStatusConfig(status);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MessageOutlined style={{ fontSize: 24, color: '#470172' }} />
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '20px', fontWeight: 500, color: '#470172' }}>
            Contact Submission
          </span>
        </div>
      }
      width={700}
      footer={[
        <Button
          key="close"
          onClick={onClose}
          style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
        >
          Close
        </Button>,
        <Button
          key="update"
          type="primary"
          loading={updating}
          onClick={handleUpdateStatus}
          disabled={status === submission.status}
          style={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '16px',
            color: '#ffffff',
            fontWeight: 400,
            backgroundColor: '#470172',
            borderColor: '#470172'
          }}
        >
          Update Status
        </Button>,
      ]}
      style={{ top: 20 }}
    >
      <div style={{ fontFamily: 'Rubik, sans-serif' }}>
        {/* Status Update Section */}
        <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
          <label style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172', display: 'block', marginBottom: 8 }}>
            Update Status
          </label>
          <Select
            value={status}
            onChange={setStatus}
            style={{ width: '100%', fontFamily: 'Rubik, sans-serif' }}
            size="large"
          >
            <Option value="new">New</Option>
            <Option value="read">Read</Option>
            <Option value="resolved">Resolved</Option>
          </Select>
        </div>

        <Divider />

        {/* Submission Details */}
        <Descriptions
          column={1}
          labelStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172' }}
          contentStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', color: '#595959' }}
        >
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 8 }} />Name</>}>
            {submission.fullName || '—'}
          </Descriptions.Item>

          <Descriptions.Item label={<><MailOutlined style={{ marginRight: 8 }} />Email</>}>
            <a href={`mailto:${submission.email}`} style={{ color: '#470172' }}>{submission.email}</a>
          </Descriptions.Item>

          <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 8 }} />Phone</>}>
            {submission.phone ? (
              <a href={`tel:${submission.phone}`} style={{ color: '#470172' }}>{submission.phone}</a>
            ) : '—'}
          </Descriptions.Item>

          <Descriptions.Item label={<><MessageOutlined style={{ marginRight: 8 }} />Message</>}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{submission.message}</div>
          </Descriptions.Item>

          <Descriptions.Item label="Current Status">
            <Tag
              style={{
                fontSize: 13,
                fontFamily: "'Rubik', sans-serif",
                fontWeight: 600,
                border: `1px solid ${statusConfig.border}`,
                background: statusConfig.bg,
                color: statusConfig.color,
                padding: '4px 14px',
                borderRadius: 6,
              }}
            >
              {statusConfig.label}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8 }} />Submitted On</>}>
            {submission.createdAt ? format(new Date(submission.createdAt), 'MMMM d, yyyy h:mm a') : '—'}
          </Descriptions.Item>

          {submission.updatedAt && submission.updatedAt !== submission.createdAt && (
            <Descriptions.Item label="Last Updated">
              {format(new Date(submission.updatedAt), 'MMMM d, yyyy h:mm a')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
    </Modal>
  );
};

export default ContactMessageDetailModal;
